import { useState } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";

const faqs = [
  {
    q: "What areas do you cover?",
    a: "We cover Caterham and the surrounding areas including Warlingham, Oxted, Redhill, Reigate, Coulsdon, Purley, South Croydon, Kenley, Whyteleafe, Banstead, Epsom, Sutton, Bromley, and Croydon.",
  },
  {
    q: "How long does installation take?",
    a: "Most window and door installations are completed within a day. Larger projects such as conservatories typically take 3–7 days depending on size and complexity. We will always give you a clear timeline before starting.",
  },
  {
    q: "Do I need planning permission?",
    a: "Most window and door replacements fall under permitted development rights and do not require planning permission. We will advise you on any planning requirements during your free survey.",
  },
  {
    q: "What guarantee do you offer?",
    a: "All our products and installations come with a comprehensive 10-year guarantee. This covers both the product and the installation workmanship.",
  },
  {
    q: "How do I get a quote?",
    a: "Simply fill in our contact form, call us on 07852 636 300, or email sales@ensurewindows.co.uk. We will arrange a free no-obligation home survey at a time that suits you.",
  },
];

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", subject: "", product: "", message: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    if (!formData.message.trim()) newErrors.message = "Message is required";
    if (formData.message.length > 500) newErrors.message = "Message must be under 500 characters";
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setSubmitting(true);
    try {
      const body = new URLSearchParams(formData as Record<string, string>);
      await fetch("https://readdy.ai/api/form/d720pp4991ove783t8c0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    }
    setSubmitting(false);
  };

  return (
    <main>
      <Navbar />

      {/* Hero */}
      <section className="relative pt-40 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/d6f87d5f6e7162d7fa932a3c9fe8c496.jpg"
            alt="Contact Ensure Windows"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-zinc-900/70"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <span className="text-amber-400 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>Get in Touch</span>
          <h1 className="text-5xl lg:text-6xl font-bold text-white mt-4 mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Contact Us
          </h1>
          <p className="text-stone-300 text-lg max-w-2xl mx-auto" style={{ fontFamily: "'Raleway', sans-serif" }}>
            Request a free quote, ask a question, or book a home survey. Our friendly team is ready to help.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: "ri-phone-line", title: "Call Us", lines: ["07852 636 300", "Mon–Fri 8am–6pm", "Sat 9am–4pm"], link: "tel:07852636300" },
              { icon: "ri-mail-line", title: "Email Us", lines: ["sales@ensurewindows.co.uk", "We reply within 24 hours", ""], link: "mailto:sales@ensurewindows.co.uk" },
              { icon: "ri-map-pin-2-line", title: "Our Address", lines: ["Seven House, Town End", "Caterham CR3 5UG", "Surrey"], link: null },
            ].map((card) => (
              <div key={card.title} className="bg-stone-50 rounded-lg p-7 border border-stone-100 text-center">
                <div className="w-14 h-14 flex items-center justify-center bg-amber-50 rounded-full mx-auto mb-4">
                  <i className={`${card.icon} text-amber-600 text-2xl`}></i>
                </div>
                <h3 className="text-lg font-bold text-zinc-800 mb-3" style={{ fontFamily: "'Raleway', sans-serif" }}>{card.title}</h3>
                {card.lines.map((line, i) => (
                  line ? (
                    card.link && i === 0 ? (
                      <a key={i} href={card.link} className="block text-zinc-600 text-sm hover:text-amber-600 transition-colors cursor-pointer" style={{ fontFamily: "'Raleway', sans-serif" }}>{line}</a>
                    ) : (
                      <p key={i} className="text-zinc-500 text-sm" style={{ fontFamily: "'Raleway', sans-serif" }}>{line}</p>
                    )
                  ) : null
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form + Map */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Form */}
            <div className="bg-white rounded-lg p-8 border border-stone-100">
              <h2 className="text-3xl font-bold text-zinc-900 mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Send Us a Message</h2>
              <p className="text-zinc-500 text-sm mb-8" style={{ fontFamily: "'Raleway', sans-serif" }}>Fill in the form below and we will be in touch within 24 hours.</p>

              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 flex items-center justify-center bg-amber-50 rounded-full mx-auto mb-5">
                    <i className="ri-check-double-line text-amber-600 text-4xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-zinc-900 mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Message Received!</h3>
                  <p className="text-zinc-500" style={{ fontFamily: "'Raleway', sans-serif" }}>Thank you for getting in touch. A member of our team will respond within 24 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-semibold text-zinc-600 mb-1.5 uppercase tracking-wide" style={{ fontFamily: "'Raleway', sans-serif" }}>Full Name *</label>
                      <input name="name" value={formData.name} onChange={handleChange} placeholder="John Smith" className={`w-full border ${errors.name ? "border-red-400" : "border-stone-200"} text-zinc-800 text-sm px-4 py-3 rounded-md focus:outline-none focus:border-amber-400`} style={{ fontFamily: "'Raleway', sans-serif" }} />
                      {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-zinc-600 mb-1.5 uppercase tracking-wide" style={{ fontFamily: "'Raleway', sans-serif" }}>Email Address *</label>
                      <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="john@example.com" className={`w-full border ${errors.email ? "border-red-400" : "border-stone-200"} text-zinc-800 text-sm px-4 py-3 rounded-md focus:outline-none focus:border-amber-400`} style={{ fontFamily: "'Raleway', sans-serif" }} />
                      {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-semibold text-zinc-600 mb-1.5 uppercase tracking-wide" style={{ fontFamily: "'Raleway', sans-serif" }}>Phone Number</label>
                      <input name="phone" value={formData.phone} onChange={handleChange} placeholder="07700 123456" className="w-full border border-stone-200 text-zinc-800 text-sm px-4 py-3 rounded-md focus:outline-none focus:border-amber-400" style={{ fontFamily: "'Raleway', sans-serif" }} />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-zinc-600 mb-1.5 uppercase tracking-wide" style={{ fontFamily: "'Raleway', sans-serif" }}>Product Interest</label>
                      <select name="product" value={formData.product} onChange={handleChange} className="w-full border border-stone-200 text-zinc-700 text-sm px-4 py-3 rounded-md focus:outline-none focus:border-amber-400" style={{ fontFamily: "'Raleway', sans-serif" }}>
                        <option value="">Select a product...</option>
                        <option>Aluminium Bifold Doors</option>
                        <option>uPVC Windows</option>
                        <option>Aluminium Windows</option>
                        <option>Composite Doors</option>
                        <option>Conservatories</option>
                        <option>Roof Lanterns</option>
                        <option>French Doors</option>
                        <option>Patio Doors</option>
                        <option>General Enquiry</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-zinc-600 mb-1.5 uppercase tracking-wide" style={{ fontFamily: "'Raleway', sans-serif" }}>Subject</label>
                    <input name="subject" value={formData.subject} onChange={handleChange} placeholder="Brief subject of your enquiry" className="w-full border border-stone-200 text-zinc-800 text-sm px-4 py-3 rounded-md focus:outline-none focus:border-amber-400" style={{ fontFamily: "'Raleway', sans-serif" }} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-zinc-600 mb-1.5 uppercase tracking-wide" style={{ fontFamily: "'Raleway', sans-serif" }}>Your Message * <span className="text-zinc-400 font-normal normal-case">(max 500 characters)</span></label>
                    <textarea name="message" value={formData.message} onChange={handleChange} rows={5} maxLength={500} placeholder="Tell us about your project, measurements, or any questions you have..." className={`w-full border ${errors.message ? "border-red-400" : "border-stone-200"} text-zinc-800 text-sm px-4 py-3 rounded-md focus:outline-none focus:border-amber-400 resize-none`} style={{ fontFamily: "'Raleway', sans-serif" }} />
                    <div className="flex justify-between items-center mt-1">
                      {errors.message ? <p className="text-red-500 text-xs">{errors.message}</p> : <span />}
                      <p className="text-zinc-400 text-xs">{formData.message.length}/500</p>
                    </div>
                  </div>
                  <button type="submit" disabled={submitting} className="w-full bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white font-semibold py-4 rounded-md transition-colors whitespace-nowrap cursor-pointer text-base" style={{ fontFamily: "'Raleway', sans-serif" }}>
                    {submitting ? "Sending your message..." : "Send Message"}
                  </button>
                </form>
              )}
            </div>

            {/* Map + Info */}
            <div className="flex flex-col gap-6">
              <div className="rounded-lg overflow-hidden h-80 flex-shrink-0">
                <iframe
                  title="Ensure Windows Map"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2490.5!2d-0.0820!3d51.2825!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sCaterham%2C+Surrey+CR3+5UG!5e0!3m2!1sen!2suk!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
              <div className="bg-white rounded-lg p-6 border border-stone-100 flex-1">
                <h3 className="text-xl font-bold text-zinc-900 mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Opening Hours</h3>
                <ul className="space-y-2">
                  {[
                    { day: "Monday – Friday", hours: "8:00am – 6:00pm" },
                    { day: "Saturday", hours: "9:00am – 4:00pm" },
                    { day: "Sunday", hours: "Closed" },
                  ].map((item) => (
                    <li key={item.day} className="flex justify-between text-sm border-b border-stone-100 pb-2 last:border-0" style={{ fontFamily: "'Raleway', sans-serif" }}>
                      <span className="text-zinc-600 font-medium">{item.day}</span>
                      <span className="text-zinc-400">{item.hours}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-amber-600 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>FAQ</span>
            <h2 className="text-4xl font-bold text-zinc-900 mt-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-stone-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer bg-white hover:bg-stone-50 transition-colors"
                >
                  <span className="font-semibold text-zinc-800 text-sm pr-4" style={{ fontFamily: "'Raleway', sans-serif" }}>{faq.q}</span>
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className={`${openFaq === i ? "ri-subtract-line" : "ri-add-line"} text-amber-600`}></i>
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 bg-stone-50">
                    <p className="text-zinc-500 text-sm leading-relaxed" style={{ fontFamily: "'Raleway', sans-serif" }}>{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
